/*
 * Copyright (c) 2023 Pegasystems Inc.
 * All rights reserved.
 */

import { publicConstants } from '../constants';
import { getTargetFromContainerItemID, getContainerItemData } from '../container/container-utils';

import TreeNode from './treenode';
import ContextNode from './contextnode';
import utils from './utils';
import type { Message, NodeProps } from './types';
import WhenNode from './whennode';

const { VIEW, FIELD, CONTEXT, DECLARATIVE_TARGET, WHEN } = publicConstants.CONTEXT_TREE_NODE_TYPES;

interface MutateObject {
  type: string;
  messages?: Message[];
  fieldName?: string;
  rowIndex?: string;
}

class ContextTreeManager {
  public rootNodes: { [key: string]: ContextNode };

  constructor() {
    this.rootNodes = {};
  }

  /**
   * @description This method checks whether the contextName passed exists as a root Node
   * @example <caption>Example for _isContextNodeExist()</caption>
   * // In this example, the API checks if the contextName passed exists as a root Node
   * this._isContextNodeExist("app/primary_1")
   * // returns  - if the contextName passed exists as a root node
   * @param contextName Name of the context we are checking for
   * @returns if the contextName passed exists as a root node
   * @function
   * @private
   */
  _isContextNodeExist(contextName: string) {
    return Object.keys(this.rootNodes).includes(contextName);
  }

  /**
   * @description This method checks if the contextName passed already exists as a root Node otherwise adds the contextNode to
   *  the tree manager as a root Node
   * @example <caption>Example for addContextNode()</caption>
   * // In this example, the API adds the given context to the Tree Manager as a root node
   * viewNode = this.addContextNode("app/primary_1")
   * // returns  - ContextNode that was added
   * @param contextName contextName of the root node we want to add
   * @returns ContextNode that was added
   * @function
   * @private
   */
  addContextNode(contextName: string) {
    if (!this._isContextNodeExist(contextName)) {
      const contextNode = new ContextNode(CONTEXT, contextName);
      this.rootNodes[contextName] = contextNode;
    }
    return this.rootNodes[contextName];
  }

  /**
   * @description This method searches for the context name provided in the root nodes and returns if the node is found
   * @example <caption>Example for getContextNode()</caption>
   * // In this example, the API gives the node we are looking for
   * contextNode = this.getContextNode("app/primary_1")
   * // returns  - The root node for the context name provided
   * @param contextName contextName of the root node we want
   * @returns The root node for the context name provided
   * @function
   * @private
   */
  getContextNode(contextName?: string | null) {
    return contextName ? this.rootNodes[contextName] : undefined;
  }

  /**
   * @description This method adds the page node to the context name provided based on the page name.
   * @example <caption>Example for addPageNode()</caption>
   * // In this example, the API adds the page node we are looking for
   * node = this.addPageNode("app/primary_1/workarea_1", "caseInfo.content")
   * // returns  - The page node for the context name, page name provided which was added
   * @param contextName context in which we want to add the page node
   * @param pageName name of the page node
   * @returns The page node for the context name, page name provided which was added
   * @function
   * @private
   */
  addPageNode(contextName: string, pageName: string) {
    return this.addContextNode(contextName).addNode('PAGE', pageName);
  }

  /**
   * @description This method adds the parentviewnode based on the context name, page name, parent view provided.
   * @example <caption>Example for _addParentViewNode()</caption>
   * // In this example, the API adds the parent view node we are looking for
   * parentViewNode = this._addParentViewNode("app/primary_1/workarea_1", "caseInfo.content", "Registrar")
   * // returns  - The parent view node for the context name, page name, parent view name provided
   * @param contextName context in which we want to add the view
   * @param pageName page in which we want to add the view
   * @param parentView name of the view we want to add
   * @returns The parent view node for the context name, page name, parent view name provided which was added
   * @function
   * @private
   */
  _addParentViewNode(contextName: string, pageName: string, parentView: string) {
    const pageNode = this.addContextNode(contextName).addNode('PAGE', pageName);
    let viewNode = pageNode.getReference(parentView);
    if (!viewNode) {
      viewNode = pageNode.addNode('VIEW', parentView);
      pageNode.addReference(parentView, viewNode);
    }
    return viewNode;
  }

  pushChildPageNodeToParentView(parentViewNode: TreeNode, pageNode: TreeNode, pageName: string) {
    const children = parentViewNode.children;
    let nodeExists = false;

    if (children) {
      for (let i = 0; i < children.length; i += 1) {
        if (children[i].name === pageName) {
          nodeExists = true;
          break;
        }
      }
    }

    if (!nodeExists) {
      children.push(pageNode);
    }
    pageNode.parent = parentViewNode;
  }

  /**
   * @description This method adds the viewnode based on the context name, page name,view name, parent view name provided.
   * @example <caption>Example for addViewNode()</caption>
   * // In this example, the API adds the view node we are looking for
   * viewNode = this.addViewNode("app/primary_1/workarea_1", "caseInfo.content", "HYDERABAD", "Registrar")
   * // returns  - The  view node for the context name, page name, view name,  parent view name provided
   * @param contextName context in which we want to add the view
   * @param pageName page in which we want to add the view
   * @param viewName name of the view we want to add
   * @param parentViewName name of the parent view in which we want to add the view
   * @returns The added view node for the context name, page name, view name, parent view name provided
   * @function
   * @private
   */
  addViewNode(contextName: string, pageName: string, viewName: string, parentViewName?: string) {
    const pageNode: TreeNode = this.addContextNode(contextName).addNode('PAGE', pageName);
    let viewNode: TreeNode | null = null;
    if (parentViewName && viewName !== parentViewName) {
      const parentViewNode = this._addParentViewNode(contextName, pageName, parentViewName);
      viewNode = parentViewNode.addNode('VIEW', viewName);
    } else {
      viewNode = pageNode.addNode('VIEW', viewName);
    }
    pageNode.addReference(viewName, viewNode);

    // link child pagenode to parent view which present in different Page if present

    // get parent page
    const parentPageNameArray: string[] = pageName.split('.');
    while (parentPageNameArray.length > 0) {
      parentPageNameArray.pop();

      const parentPageName: string = parentPageNameArray.join('.');
      // get the parent view from references
      const parentPageNode = this.getContextNode(contextName)?.getNode('PAGE', parentPageName);
      // push child page node to view children if the node is not exist
      const parentViewNodeInParentPage = parentPageNode?.getReference(parentViewName);

      if (parentViewNodeInParentPage) {
        this.pushChildPageNodeToParentView(parentViewNodeInParentPage, pageNode, pageName);
        break;
      }
    }
    return viewNode;
  }

  /**
   * @description This method gets the viewnode based on the context name, page name,view name provided.
   * @example <caption>Example for getViewNode()</caption>
   * // In this example, the API gives the view node we are looking for
   * viewNode = this.getViewNode("app/primary_1/workarea_1", "caseInfo.content", "HYDERABAD")
   * // returns  - The  view node for the context name, page name, view name provided
   * @param contextName context from which we want to get the view
   * @param pageName from which we want to get the view
   * @param viewName name of the view we want
   * @returns The view node for the context name, page name, view name provided
   * @function
   * @private
   */
  getViewNode(contextName: string, pageName: string, viewName: string) {
    return this.getContextNode(contextName)?.getNode('PAGE', pageName)?.getReference(viewName);
  }

  /**
   * @description This method adds the page list node based on the context name, page name,view name, page list name provided.
   * @example <caption>Example for addPageListNode()</caption>
   * // In this example, the API adds the page list node we are looking for
   * fieldNode = this.addPageListNode("app/primary_1/workarea_1", "caseInfo.content", "TOP_EmbeddedData_5", ".EmbeddedData")
   * // returns  - The  page list node for the context name, page name, view name, page list name provided
   * @param contextName contextName of the page list node we want to add
   * @param pageName pageName of the page list node we want to add
   * @param viewName viewName of the page list node we want to add
   * @param pageListName pageListName of the page list node we want to add
   * @returns The added page list node for the context name, page name, view name, page list name provided
   * @function
   * @private
   */
  addPageListNode(contextName: string, pageName: string, viewName: string, pageListName: string, options: NodeProps) {
    if (!pageListName) {
      return undefined;
    }
    if (pageListName.startsWith('.')) {
      pageListName = pageListName.substring(1);
    }
    const contextNode = this.addContextNode(contextName);
    const fieldNode = contextNode
      .addNode('PAGE', pageName)
      ?.getReference(viewName)
      ?.addNode('PAGELIST', `${pageName}.${pageListName}`, options);

    if (fieldNode) {
      contextNode.addReference(`${pageName}.${pageListName}`, fieldNode);
    }
    return fieldNode;
  }

  /**
   * @description - This API updates all the fields present in the pageList property in the contextTree.
   * @param contextName - name of the context in which pagelist field present.
   * @param pageName - pagereference of the pagelist field which needs to be updated.
   * @param pageListName - name of the pagelist field which needs to be updated.
   * @param length - Length of the pagelist array that came as response. It will be taken from redux store.
   * @returns {void}
   * @private
   */
  updatePageListNode(contextName: string, pageName: string, pageListName: string, length: number) {
    if (!pageListName) {
      return;
    }
    if (pageListName.startsWith('.')) {
      pageListName = pageListName.substring(1);
    }
    const contextNode = this.addContextNode(contextName);
    contextNode.removePageListNodes(`${pageName}.${pageListName}`, length);
  }

  removeFieldNode(contextName: string, pageReference: string, viewName: string, fieldName: string, index: number) {
    // remove node from view children
    const contextNode = this.getContextNode(contextName);
    contextNode
      ?.getNode('PAGE', pageReference)
      ?.getReference(viewName)
      ?.removeNode(`${pageReference}${fieldName}`, index);
    // remove node from references
    contextNode?.removeReference(`${pageReference}${fieldName}`, FIELD, index);
    contextNode?.removeReference(`${pageReference}${fieldName}`, DECLARATIVE_TARGET, index);
  }

  removeViewNode(contextName: string, pageReference: string, viewName: string, index?: number) {
    const contextNode = this.getContextNode(contextName);
    const pageNode = contextNode?.getNode('PAGE', pageReference);
    const viewNode = pageNode?.getReference(viewName);
    this.removeChildViewNodes(contextName, pageReference, viewName, index);
    const nodeIndex: number =
      viewNode?.parent?.children?.findIndex((node: { name: string }) => node.name === viewName) ?? -1;
    if (nodeIndex > -1) {
      viewNode?.parent?.children.splice(nodeIndex, 1);
    }
  }

  removeChildViewNodes(contextName: string, pageReference: string, viewName: string, index?: number) {
    const contextNode = this.getContextNode(contextName) as ContextNode;
    const pageNode = contextNode?.getNode('PAGE', pageReference);
    const viewNode = pageNode?.getReference(viewName);
    viewNode?.children.forEach((node) => {
      if (node.type === 'PAGE') {
        this.removeViewNode(contextName, node.name, viewName, index);
      } else if (node.type === VIEW) {
        this.removeChildViewNodes(contextName, pageReference, node.name, index);
      } else {
        contextNode.removeReference(node.name, FIELD, node.props.index);
        contextNode.removeReference(node.name, DECLARATIVE_TARGET, node.props.index);
        if (node.type === 'PAGELIST' || node.props?.componentName === 'Address') {
          this.deleteAllListNodes(contextName, node.name);
        }
      }
    });
    pageNode?.references.delete(viewName);
  }

  deleteAllListNodes(contextName: string, pageReference: string, deleteParentListNode = true) {
    const deleteAllNodeReference = (type: string) => {
      const contextNode = this.getContextNode(contextName);
      const fieldReferences = contextNode?.references?.get(type);

      if (fieldReferences) {
        for (const [key] of fieldReferences) {
          if (
            (deleteParentListNode && key === pageReference) ||
            key.startsWith(`${pageReference}.`) ||
            key.startsWith(`${pageReference}[`)
          ) {
            contextNode?.references?.get(type)?.delete(key);
          }
        }
      }
    };
    deleteAllNodeReference(FIELD);
    deleteAllNodeReference(DECLARATIVE_TARGET);
  }

  deleteListNode(contextName: string, nodeKey: string[], index: number) {
    const deleteNodeReference = (type: string) => {
      const contextNode = this.getContextNode(contextName);
      const fields = contextNode?.references?.get(type);
      if (fields) {
        for (const [key] of fields) {
          if (key.includes(`${nodeKey.join('.')}[${index}]`)) {
            contextNode?.references?.get(type)?.delete(key);
          }
        }
      }
    };
    deleteNodeReference(FIELD);
    deleteNodeReference(DECLARATIVE_TARGET);
  }

  /**
   * @description This method gives the page list node based on the context name, page name,view name, page list name provided.
   * @example <caption>Example for getPageListNode()</caption>
   * // In this example, the API gives the page list node we are looking for
   * pagelistnode = this.getPageListNode("app/primary_1/workarea_1", "caseInfo.content", "TOP_EmbeddedData_5", ".EmbeddedData")
   * // returns  - The  page list node for the context name, page name, view name, page list name provided
   * @param contextName contextName of the page list node we want
   * @param pageName pageName of the page list node we want
   * @param viewName viewName of the page list node we want
   * @param pageListName pageListName of the page list node we want
   * @returns The page list node for the context name, page name, view name, page list name provided
   * @function
   * @private
   */
  getPageListNode(contextName: string, pageName: string, viewName: string, pageListName: string) {
    const references = this.getContextNode(contextName)?.getNode('PAGE', pageName)?.getReference(viewName) as TreeNode;
    return references?.children.find(
      (node: TreeNode) => node.type === 'PAGELIST' && node.name === `${pageName}.${pageListName}`
    );
  }

  /**
   * @description This method gives the field node based on the context name, page name,view name, field name provided.
   * @example <caption>Example for getFieldNode()</caption>
   * // In this example, the API gets the field node we are looking for
   * fieldNode = this.getFieldNode("app/primary_1/workarea_1", "caseInfo.content", "TOP_EmbeddedData_5", ".EmpName")
   * // returns  - The  field node for the context name, page name, view name, field name provided
   * @param contextName contextName of the field node we want
   * @param pageName pageName of the field node we want
   * @param viewName viewName of the field node we want
   * @param fieldName fieldName of the field node we want
   * @returns The field node for the context name, page name, view name, field name provided
   * @function
   * @private
   */
  getFieldNode(contextName: string, pageName: string, viewName: string, fieldName: string) {
    return this.getViewNode(contextName, pageName, viewName)?.getNode(FIELD, fieldName);
  }

  _findViewNode(contextName: string, pageName: string, viewName: string, contextNode: ContextNode) {
    let referenceNode = contextNode.addNode('PAGE', pageName)?.getReference(viewName);
    if (referenceNode) {
      return referenceNode;
    }

    const pageTokens = pageName.split('.');
    while (pageTokens.length > 0) {
      pageTokens.pop();
      const pageRef = pageTokens.join('.');
      referenceNode = contextNode.addNode('PAGE', pageRef)?.getReference(viewName);
      if (referenceNode) {
        return referenceNode;
      }
    }
    return this.addViewNode(contextName, pageName, viewName);
  }

  /**
   * @description This method adds the field node based on the context name, page name,view name, field name provided.
   * @example <caption>Example for addFieldNode()</caption>
   * // In this example, the API adds the field node we are looking for
   * fieldNode = this.addFieldNode("app/primary_1/workarea_1", "caseInfo.content", "TOP_EmbeddedData_5", ".EmpName")
   * // returns  - The  field node for the context name, page name, view name, field name provided
   * @param contextName contextName of the field node we want to add
   * @param pageName pageName of the field node we want to add
   * @param viewName viewName of the field node we want to add
   * @param fieldName fieldName of the field node we want to add
   * @returns The added field node for the context name, page name, view name, field name provided
   * @function
   * @private
   */
  addFieldNode(
    contextName: string,
    pageName: string,
    viewName: string,
    fieldName: string,
    value: string,
    options: NodeProps = {}
  ) {
    const contextNode = this.addContextNode(contextName);
    const referenceNode = this._findViewNode(contextName, pageName, viewName, contextNode);
    const fieldNode = this.createFieldNode(referenceNode, pageName, fieldName, options);

    if (fieldNode) {
      this.updateFieldNode(contextNode, referenceNode, fieldNode, contextName, pageName, fieldName, value);
    }

    return fieldNode;
  }

  createFieldNode(
    referenceNode: TreeNode,
    pageName: string,
    fieldName: string,
    options: NodeProps
  ): TreeNode | undefined {
    const { isDeclarativeTarget } = options;
    if (isDeclarativeTarget) {
      return referenceNode.addNode(DECLARATIVE_TARGET, `${pageName}${fieldName}`, options);
    }
    return referenceNode.addNode(FIELD, `${pageName}${fieldName}`, options);
  }

  updateFieldNode(
    contextNode: ContextNode,
    referenceNode: TreeNode,
    fieldNode: TreeNode,
    contextName: string,
    pageName: string,
    fieldName: string,
    value: string
  ) {
    const fieldReferenceWithPageName = `${pageName}${fieldName}`;
    if (fieldNode.props?.componentName === 'Address') {
      this.deleteAllListNodes(contextName, fieldNode.name);
    }
    contextNode.addReference(fieldReferenceWithPageName, fieldNode);

    fieldNode.value = value;
    fieldNode.setPreviousDefaultValue();

    if (fieldNode.props.isInsideList) {
      this.handleFieldInsideList(contextNode, referenceNode, contextName, pageName, fieldName);
    }
  }

  handleFieldInsideList(
    contextNode: ContextNode,
    referenceNode: TreeNode,
    contextName: string,
    pageName: string,
    fieldName: string
  ) {
    const target = getTargetFromContainerItemID(contextName);
    const itemData = getContainerItemData(target, contextName) || {};
    const fullReference = `${pageName}${fieldName}`;
    let pageGroupProperty = null;
    if (itemData?.subscriptFieldName || itemData?.isPageGroup) {
      pageGroupProperty =
        pageName.substring(0, pageName.lastIndexOf(`.${itemData?.index ?? ''}`)) ||
        fullReference.substring(0, fullReference.lastIndexOf(`.${itemData?.index}`));
      this.createListNodeIfNotExist(contextNode, referenceNode, pageGroupProperty);
    } else {
      const listProperty =
        pageName.substring(0, pageName.lastIndexOf('[')) || fullReference.substring(0, fullReference.lastIndexOf('['));
      this.createListNodeIfNotExist(contextNode, referenceNode, listProperty);
    }
  }

  createListNodeIfNotExist(contextNode: ContextNode, referenceNode: TreeNode, property: string) {
    let listNode = contextNode.getReference(property, FIELD);
    if (!listNode) {
      listNode = referenceNode.addNode(FIELD, property, {});
      contextNode.addReference(property, listNode);
    }
  }

  /**
   * @description This method remove the root node based on the context name provided
   * @example <caption>Example for removeContextTreeNode()</caption>
   * // In this example, the API removes the root node based on the context name provided
   * this.removeContextTreeNode("app/primary_1/workarea_1")
   * // returns  - if the root node can be removed based on context name provided
   * @param contextName context name of the root node to be removed
   * @returns if the root node can be removed based on context name provided
   * @function
   * @private
   */
  removeContextTreeNode(contextName: string) {
    delete this.rootNodes[contextName];
  }

  /**
   * @description This method adds a callback to be executed whenever any mutation occurs to any of the view's children .
   * @example <caption>Example for onViewMutate()</caption>
   * // In this example, the API registers a callback to listen to any changes/mutations in the current view's hierarchy
   * PCore.getContextTreeManager().onViewMutate("app/primary_1/workarea_1", "caseInfo.content", "View-Name", (errors) => {
   *    setErrorStateOnView((fieldErrors) => {
   *    const errors = [...fieldErrors];
   * ...
   * }
   * })
   * @param context context of the view we want to listen for changes/mutation that might occur on fields inside the current view hierarchy
   * @param pageName page Name of the view we want to listen for changes/mutation that might occur on fields inside the current view hierarchy
   * @param viewName view Name of the view we want to listen for changes/mutation that might occur on fields inside the current view hierarchy
   * @param callback callback to be executed on mutation i.e when mutateField() is called on any field inside this view hierarchy. Callbacks can generally be used to modify the view state
   * @function
   * @public
   */
  onViewMutate(context: string, pageName: string, viewName: string, callback: (object: { type: string }) => void) {
    this.getViewNode(context, pageName, viewName)?.addEventListener(callback);
  }

  /**
   * @description This method triggers the callback that have been registered using onViewMutate on any of its parent views.
   * This method is usually called when we want to propagate the changes on the field to its parent views.
   * @example <caption>Example for mutateField()</caption>
   * // In this example, triggers the callback set on any of its parent views
   * PCore.getContextTreeManager().mutateField("app/primary_1/workarea_1", "caseInfo.content", ".FieldName",
   * [{fieldName: "FieldName",type:"error",message:"Field can't be blank"}]);
   * @param context context in which field is present
   * @param pageName name of the page in which field is present
   * @param fieldName name of the field on which mutation has occurred
   * @param mutateObject mutation object which needs to be passed as a parameter to the callback
   * @param mutateObject.type type of the mutation object
   * @function
   * @public
   */
  mutateField(
    context: string | undefined | null,
    pageName: string | undefined,
    fieldName: string,
    mutateObject: { type: string; messages?: Message[]; fieldName: string }
  ) {
    const contextNode = this.getContextNode(context);
    const [pageNameWithoutPagelist, pageListName, rowIndex] = utils.getPageListNameAndIndexFromPageName(pageName);
    if (fieldName.startsWith('.')) {
      fieldName = fieldName.substring(1);
    }
    if (pageListName) {
      this.mutatePageList(context, pageNameWithoutPagelist, pageListName, { ...mutateObject, rowIndex });
    }
    const fieldNode: TreeNode | WhenNode | undefined = contextNode?.getReference(`${pageName}.${fieldName}`, FIELD);
    if (fieldNode && fieldNode instanceof TreeNode) {
      fieldNode.triggerEvent(mutateObject);
    }
  }

  /**
   * @description This method adds a callback to be executed whenever any mutation occurs to any of the page list's children.
   * @example <caption>Example for onPageListMutate()</caption>
   * // In this example,  the API registers a callback to listen to any changes/mutations in the current pagelist's hierarchy
   * PCore.getContextTreeManager().onPageListMutate("app/primary_1/workarea_1", "caseInfo.content", "View-Name", ".PageListName", (errors) => {
   *    setErrorStateOnPagelist((fieldErrors) => {
   *    const errors = [...fieldErrors];
   * ...
   * }
   * })
   * @param context context of the pagelist we want to listen for changes/mutation that might occur on fields inside the current pagelist hierarchy
   * @param pageName page Name of the pagelist we want to listen for changes/mutation that might occur on fields inside the current pagelist hierarchy
   * @param viewName view Name of the pagelist we want to listen for changes/mutation that might occur on fields inside the current pagelist hierarchy
   * @param pageListName name of the pagelist we want to listen for changes/mutation that might occur on fields inside the current pagelist hierarchy
   * @param callback callback to be executed on mutation i.e when mutatePageList() is called on any field inside this pagelist hierarchy. Callbacks can generally be used to modify the pagelist's view state
   * @function
   * @public
   */
  onPageListMutate(
    context: string,
    pageName: string,
    viewName: string,
    pageListName: string,
    callback: (object: { type: string }) => void
  ) {
    this.getPageListNode(context, pageName, viewName, pageListName)?.addEventListener(callback);
  }

  /**
   * @description This method triggers the callback that have been registered using onPageListMutate on any of its parent views.
   * This method is usually called when we want to propagate the changes on the pagelist fields to its parent views.
   * @example <caption>Example for mutatePageList()</caption>
   * // In this example, triggers the callback set on any of its parent views
   * PCore.getContextTreeManager().mutatePageList("app/primary_1/workarea_1", "caseInfo.content", "Page-List-Name",
   * [{fieldName: "Employees",type:"error",message:"Employees can't be blank"}]);
   * @param context context in which field is present
   * @param pageName name of the page in which field is present
   * @param pageListName name of the pagelist on which mutation has occurred
   * @param mutateObject mutation object which needs to be passed as a parameter to the callback
   * @param mutateObject.type type of the mutation object
   * @param stopPropagation stop propagating the event to parent
   * @function
   * @public
   */
  mutatePageList(
    context: string | undefined | null,
    pageName: string | undefined,
    pageListName: string | undefined,
    mutateObject: MutateObject,
    stopPropagation = false
  ) {
    const contextNode = this.getContextNode(context);
    const fieldNode: TreeNode | WhenNode | undefined = contextNode?.getReference(`${pageName}.${pageListName}`, FIELD);
    if (fieldNode && fieldNode instanceof TreeNode) {
      fieldNode.triggerEvent(mutateObject, stopPropagation);
    }
  }

  /**
   * This method gives the list of Editable & fields which are present in the provided context.
   * @param context - name of the context
   * @returns
   */
  getFieldsList(context: string): Map<string, TreeNode> {
    const contextNode: ContextNode | undefined = this.getContextNode(context);
    return contextNode?.references.get(FIELD) as Map<string, TreeNode>;
  }

  /**
   * This method gives the list of Editable & calculated fields which are present in the provided context.
   * @param context - name of the context
   * @param propertyReference - unique key for identifying the property
   * @function
   * @private
   */
  getFieldReferenceForProperty(context: string, propertyReference: string): TreeNode {
    let fieldNode: TreeNode = this.getContextNode(context)?.references?.get(FIELD)?.get(propertyReference) as TreeNode;
    if (!fieldNode) {
      fieldNode = this.getContextNode(context)?.references?.get(DECLARATIVE_TARGET)?.get(propertyReference) as TreeNode;
    }
    return fieldNode;
  }

  // TODO : change the function name to "getFieldReferenceCurrentValue"
  getFieldReferencePreviousValue(context: string, propertyReference: string) {
    let fieldNode: TreeNode = this.getContextNode(context)?.references?.get(FIELD)?.get(propertyReference) as TreeNode;
    if (!fieldNode) {
      fieldNode = this.getContextNode(context)?.references?.get(DECLARATIVE_TARGET)?.get(propertyReference) as TreeNode;
    }
    return fieldNode?.value;
  }

  updateFieldNodeValue(context: string, propertyReference: string, currentValue: string | number | boolean | null) {
    const fieldNode: TreeNode | WhenNode | undefined = this.getContextNode(context)
      ?.references?.get(FIELD)
      ?.get(propertyReference);

    if (fieldNode instanceof TreeNode && fieldNode) {
      fieldNode.previousValue = fieldNode.value;

      if (fieldNode.value === undefined) {
        fieldNode.previousValue = currentValue;
      }
      fieldNode.value = currentValue;
    }
  }

  /**
   * @description - This methods retrieves the map of fields which are present in the context.
   * @param context - name of the context
   * @returns This returns the Map of fields present in the provided context.
   * @example <caption>Example usage for getContextReferences </caption>
   * ContextTreeManager.getContextReferences("app/primary_1/workarea_1")
   * @private
   */
  getContextReferences(context: string | null): Map<string, TreeNode> | undefined {
    if (!this.getContextNode(context)) {
      return;
    }
    const fieldReferences = this.getContextReferencesByType(context, FIELD);
    const declarativeTargetReferences = this.getContextReferencesByType(context, DECLARATIVE_TARGET);
    return new Map([...fieldReferences.entries(), ...declarativeTargetReferences.entries()]);
  }

  /**
   * @description - This methods retrives either the fields or whens or declarative targets which are present in the context.
   * @param context - name of the context
   * @returns {Map<string, TreeNode | WhenNode>} - This returns the Map of fields present in the provided context.
   * @example <caption>Example usage for getContextReferencesByType </caption>
   * ContextTreeManager.getContextReferencesByType("app/primary_1/workarea_1", "FIELD")
   * @private
   */
  getContextReferencesByType(context: string | null, type: string): Map<string, TreeNode> {
    return this.getContextNode(context)?.references.get(type) || new Map();
  }

  isFieldExists(context: string, propertyReference: string) {
    return (
      !!this.getContextReferencesByType(context, FIELD)?.get(propertyReference) ||
      !!this.getContextReferencesByType(context, DECLARATIVE_TARGET)?.get(propertyReference)
    );
  }

  /**
   * @description - This method adds the when node based on the context name, page name,reference name, field name and type provided.
   * @example <caption>Example for addWhenNode()</caption>
   * // In this example, the API adds the when node we are looking for
   * fieldNode = ContextTreeManager.addWhenNode("app/primary_1/workarea_1", "caseInfo.content", 'FIELD', "FirstName", ".Always")
   * // returns  - The  when node for the context name, page name, view name, field name provided.
   * @param {string} contextName contextName of the when node we want to add.
   * @param {string} subType - type of the reference that when is configured. 'FIELD' and VIEW are available sub types.
   * @param {string} pageName pageName of the when node we want to add.
   * @param {string} referenceName viewName or when name of the when node we want to add.
   * @param {string} whenName name of the when node we want to add.
   * @returns {TreeNode} The added when node for the context name, page name, reference name, field name and type provided.
   * @function
   * @private
   */
  addWhenNode(
    contextName: string,
    pageName: string,
    subType: string,
    referenceName: string,
    whenName: string,
    options?: NodeProps
  ) {
    const contextNode = this.getContextNode(contextName);
    contextNode?.addWhen(pageName, subType, referenceName, whenName, options);
  }

  /**
   * @description - Retrieves fields and views configured with when condition from the provided context.
   * @param {object} context The context containing the configuration.
   * @returns {Array<{ name: string; pageReference: string }>} An array of objects representing fields configured with declarative target.
   * @function
   * @example <caption>Usage of getWhens</caption>
   * ContextTreeManager.getDeclarativeTargets('app/primary_1/workarea_1')
   * @private
   */
  getDeclarativeTargets(context: string): { name: string; pageReference: string }[] {
    const contextNode = this.getContextNode(context);
    const declarativeTargetMap =
      (contextNode?.references?.get(DECLARATIVE_TARGET) as Map<string, TreeNode>) || new Map();
    const declarativeTargetFields: { name: string; pageReference: string }[] = [];
    declarativeTargetMap.forEach((value: TreeNode | WhenNode) => {
      const props = value?.props || {};
      const { propertyName = '' } = props;
      const pageReference = props.pageReference ?? publicConstants.CASE_INFO.CASE_INFO_CONTENT;
      declarativeTargetFields.push({
        pageReference,
        name: propertyName
      });
    });
    return declarativeTargetFields;
  }

  /**
   * @description - Retrieves fields and views configured with when condition from the provided context.
   * @param {object} context The context containing the configuration.
   * @returns {Array<{ name: string; pageReference: string; referenceName: string; type: string }>} An array of objects representing fields and views configured with when condition.
   * @function
   * @example <caption>Usage of getWhens</caption>
   * ContextTreeManager.getWhens('app/primary_1/workarea_1')
   * @private
   */
  getWhens(context: string): { name: string; pageReference: string; referenceName: string; type: string }[] {
    const contextNode = this.getContextNode(context);
    const whenMap = (contextNode?.references?.get(WHEN) as Map<string, WhenNode>) || new Map();
    const whens: { name: string; pageReference: string; referenceName: string; type: string }[] = [];
    whenMap.forEach((value: WhenNode) => {
      const type: string = value.subType;
      const referenceName = value.referenceName;
      const name: string = value.name;
      const pageReference = value?.props?.pageReference ?? publicConstants.CASE_INFO.CASE_INFO_CONTENT;
      whens.push({
        name,
        pageReference,
        referenceName,
        type
      });
    });
    return whens;
  }
}

export default new ContextTreeManager();
